import React, { useContext, useEffect } from "react";
import { Route as RouterRoute, Redirect } from "react-router-dom";

import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { getDefaultRoute } from "./defaultRoute";

/*
 * Ultima tela aberta, para o F5 nao jogar a pessoa em Atendimentos.
 * =============================================================
 * Ao recarregar, a sessao leva um instante para ser validada. Nesse intervalo
 * a rota privada mandava para o login e, quando a sessao voltava, o login
 * mandava para a tela padrao — o endereco original se perdia no meio.
 *
 * Esperar a validacao resolve o caso comum, mas as rotas privadas ficam fora
 * do <Switch> (estao dentro de um provider), entao todas sao avaliadas a cada
 * renderizacao e a ordem nem sempre ajuda. Guardar a ultima tela torna a
 * correcao independente dessa corrida.
 *
 * sessionStorage, e nao localStorage: vale para esta aba e some ao fechar o
 * navegador, que e o comportamento esperado de "onde eu estava".
 * =============================================================
 */
const CHAVE_ULTIMA_TELA = "rsdesk:ultima-tela";

const guardarUltimaTela = caminho => {
  try {
    if (caminho && caminho !== "/login") sessionStorage.setItem(CHAVE_ULTIMA_TELA, caminho);
  } catch (err) {
    /* modo restrito */
  }
};

const lerUltimaTela = () => {
  try {
    return sessionStorage.getItem(CHAVE_ULTIMA_TELA) || "";
  } catch (err) {
    return "";
  }
};

const Route = ({
  component: Component,
  isPrivate = false,
  requiredProfile,
  requiredAnySpecialPermissions = [],
  requiredAnyPermissions = [],
  ...rest
}) => {
  const { isAuth, loading, user } = useContext(AuthContext);
  const defaultRoute = getDefaultRoute(user);
  const caminhoAtual = rest.location?.pathname || window.location.pathname;

  // Registra a tela em que a pessoa esta, so quando ela de fato esta nela.
  useEffect(() => {
    if (isAuth && isPrivate && rest.computedMatch) {
      guardarUltimaTela(caminhoAtual);
    }
  }, [isAuth, isPrivate, caminhoAtual, rest.computedMatch]);

  const telaDeVolta = () => {
    const ultima = lerUltimaTela();
    return ultima && ultima !== "/login" ? ultima : defaultRoute;
  };

  /*
   * Enquanto a sessao esta sendo validada, nao decide nada.
   *
   * Ao recarregar a pagina, isAuth comeca falso ate o token ser renovado. Sem
   * esta espera, a rota privada mandava para o login nesse intervalo e, quando
   * a sessao voltava, o login mandava para a tela padrao — por isso qualquer
   * F5 caia em Atendimentos, perdendo a tela onde a pessoa estava.
   */
  if (loading) {
    return <BackdropLoading />;
  }

  if (!isAuth && isPrivate) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
      </>
    );
  }

  if (isAuth && !isPrivate && !(user?.mustChangePassword && rest.path === "/login")) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: telaDeVolta(), state: { from: rest.location } }} />
      </>
    );
  }

  if (isAuth && isPrivate && user?.mustChangePassword) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
      </>
    );
  }

  const allowedProfiles = Array.isArray(requiredProfile)
    ? requiredProfile
    : requiredProfile
      ? [requiredProfile]
      : [];

  const hasSpecialPermission = requiredAnySpecialPermissions.some(permission =>
    user?.specialPermissions?.[permission] === true
  );
  const hasProfilePermission = requiredAnyPermissions.some(permission =>
    user?.permissions?.[permission] === true
  );

  if (
    isAuth &&
    (allowedProfiles.length || requiredAnyPermissions.length) &&
    !allowedProfiles.includes(user?.profile) &&
    !hasSpecialPermission &&
    !hasProfilePermission
  ) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: defaultRoute, state: { from: rest.location } }} />
      </>
    );
  }

  return (
    <>
      {loading && <BackdropLoading />}
      <RouterRoute {...rest} component={Component} />
    </>
  );
};

export default Route;
